import { HttpParams } from '@angular/common/http';
import { PageEvent } from '@angular/material/paginator';
import { Auth } from '@angular/fire/auth';
import { useQuery, useQueries } from '@tanstack/react-query';
import { auth } from '@core/utils/firebase/firebase';
import { createMenusApi } from '../api/menus.api';
import { PaginationInformation } from '@shared/pagination-server-side/datasources/genericBE.datasource';
import type { Menu, MenuWithSite, MenuDayForDateResponse } from '@core/interfaces/api';

type SortOrder = 'asc' | 'desc';

const menusApi = createMenusApi(auth as Auth);

export function useSiteMealPlansQuery({
  organizationId,
  page,
  rowsPerPage,
  sortField,
  sortOrder,
  search,
  siteName,
  onlyDefault,
  enabled = true,
}: {
  organizationId?: number | null;
  page: number;
  rowsPerPage: number;
  sortField: string;
  sortOrder: SortOrder;
  search?: string;
  siteName?: string;
  onlyDefault?: boolean;
  enabled?: boolean;
}) {
  return useQuery<{ data: MenuWithSite[]; totalCount: number }>({
    queryKey: [
      'menu-site-meal-plans',
      organizationId,
      page,
      rowsPerPage,
      sortField,
      sortOrder,
      search,
      siteName,
      onlyDefault,
    ],
    enabled: enabled && !!organizationId,
    placeholderData: (previousData) => previousData,
    queryFn: async () => {
      if (!organizationId) {
        return { data: [] as MenuWithSite[], totalCount: 0 };
      }

      let params = new HttpParams({
        fromObject: {
          sortBy: sortField,
          sortDirection: sortOrder,
          offset: (page * rowsPerPage).toString(),
          limit: rowsPerPage.toString(),
        },
      });

      if (search) {
        params = params.append('search', search);
      }

      const pagination: PageEvent = {
        length: 0,
        pageIndex: page,
        pageSize: rowsPerPage,
      };

      const paginationInformation: PaginationInformation = {
        filters: {
          search,
        },
        sort: {
          active: sortField,
          direction: sortOrder,
        },
        pagination,
      };

      return menusApi.getSiteMealPlans(
        organizationId,
        params,
        paginationInformation,
        siteName,
        onlyDefault,
      );
    },
  });
}

export function useMenuForDateQuery({
  siteId,
  date,
  enabled = true,
}: {
  siteId?: number | null;
  date?: string | null;
  enabled?: boolean;
}) {
  return useQuery<MenuDayForDateResponse | null>({
    queryKey: ['menu-for-date', siteId, date],
    enabled: enabled && !!siteId && !!date,
    queryFn: async () => {
      if (!siteId || !date) return null;
      return menusApi.getMenuForDate(siteId, date);
    },
  });
}

/** Fetch menu data for multiple dates in parallel. Returns results in the same order as `dates`. */
export function useMenuForDatesQuery({
  siteId,
  dates,
  enabled = true,
}: {
  siteId?: number | null;
  dates: string[];
  enabled?: boolean;
}) {
  return useQueries({
    queries: dates.map((date) => ({
      queryKey: ['menu-for-date', siteId, date] as const,
      enabled: enabled && !!siteId && !!date,
      queryFn: async (): Promise<MenuDayForDateResponse | null> => {
        if (!siteId || !date) return null;
        return menusApi.getMenuForDate(siteId, date);
      },
    })),
  });
}

export function useMenuQuery({
  siteId,
  cycleStartDate,
  enabled = true,
}: {
  siteId?: number | null;
  cycleStartDate?: string;
  enabled?: boolean;
}) {
  return useQuery({
    queryKey: [
      'menu-site-menu',
      siteId,
      cycleStartDate,
    ],
    enabled: enabled && !!siteId,
    queryFn: async () => {
      if (!siteId) {
        return null as Menu | null;
      }

      return menusApi.getMenu(siteId, cycleStartDate);
    },
  });
}
